import RateDisplay from "@components/Cards/RateDisplay";
import { useFormatContext } from "@lib/hooks/format-context";
import { CellContext, HeaderContext } from "@tanstack/react-table";
import { useNewVsReturningState } from "@lib/hooks/url-state";
import { getCorrelation, getTotalValueOfAllRows } from "./calculations";

type Props<T> = {
  compared?: boolean;
} & (
  | {
      info: CellContext<T, number | undefined>;
      calculateTotals?: false;
      total?: never;
      comparedTotal?: never;
    }
  | {
      info: HeaderContext<T, number | undefined>;
      calculateTotals: true;
      total?: never;
      comparedTotal?: never;
    }
  | {
      total: number;
      comparedTotal?: number;
      info?: never;
      calculateTotals?: never;
    }
);

export type CurrencyCalculatorType = (
  value: number,
  numDigits?: number
) => string;

const getDisplayString = (
  value: number,
  formatNumber: CurrencyCalculatorType
) => {
  return `${formatNumber(value * 100, 2)}%`;
};

function NewVisitorRateTableDisplay<T extends Record<string, any>>({
  info,
  compared,
  calculateTotals,
  total,
  comparedTotal,
}: Props<T>) {
  const { formatNumber } = useFormatContext();
  const { newVsReturning } = useNewVsReturningState();
  let displayNumbers = false;
  let comparedValue = 0;
  let value = 0;

  if (newVsReturning === "returning") {
    return "-";
  }

  if (newVsReturning === "new") {
    return "100%";
  }

  if (total !== undefined) {
    value = total;
    comparedValue = comparedTotal ?? 0;
    displayNumbers = value > 0 || comparedValue > 0;
  } else if (calculateTotals) {
    const allNewPageviewCount = info.column
      .getFacetedRowModel()
      .rows.map((row) => {
        return {
          value: row.original?.["nvr_upv"],
          compared: row.original?.compared?.["nvr_upv"],
        };
      });
    const allPageviewValues = info.column
      .getFacetedRowModel()
      .rows.map((row) => {
        return {
          value: row.original?.["pageview"],
          compared: row.original?.compared?.["pageview"],
        };
      });
    const newPageviewTotals = getTotalValueOfAllRows({
      values: allNewPageviewCount,
    });
    const pageviewTotals = getTotalValueOfAllRows({
      values: allPageviewValues,
    });
    if (
      newPageviewTotals.value >= 1 ||
      (newPageviewTotals.compared ?? 0) >= 1
    ) {
      displayNumbers = true;
    }

    value =
      pageviewTotals.value && newPageviewTotals.value
        ? newPageviewTotals.value / pageviewTotals.value
        : 0;
    comparedValue =
      pageviewTotals.compared && newPageviewTotals.compared
        ? newPageviewTotals.compared / pageviewTotals.compared
        : 0;
  } else if (info) {
    value = info.getValue() ?? 0;
    if (compared && info.row.original?.compared) {
      comparedValue = info.row.original?.compared[info.column?.id];
    }

    if (value || comparedValue) {
      displayNumbers = true;
    }
  }

  if (compared) {
    const rate = getCorrelation(value, comparedValue, 4);
    return (
      <div className="flex items-center w-full">
        <div className="flex-1">
          <p>{displayNumbers ? getDisplayString(value, formatNumber) : "-"}</p>
          {displayNumbers ? (
            <p className="text-[11px] leading-tight opacity-70">
              vs {getDisplayString(comparedValue, formatNumber)}
            </p>
          ) : null}
        </div>
        {displayNumbers ? (
          <RateDisplay rate={rate} isIncreasing={rate > 0} size="small" />
        ) : null}
      </div>
    );
  }

  return displayNumbers ? getDisplayString(value, formatNumber) : "-";
}

export default NewVisitorRateTableDisplay;
